import './navigation.scss';

import { disableBodyScroll, enableBodyScroll } from 'body-scroll-lock';
import debounce from 'lodash.debounce';

class Navigation {
    constructor (element, options) {
        const defaults = {
            initAttr: 'data-nav',
            rootToggleAttr: 'rootToggle',
            childrenAttr: 'category',
            scrollerAttr: 'scroller',
            subScrollerAttr: 'sub-scroller',
            toggleAttr: 'toggle',
            closeAttr: 'close',
            a11yLabelAttr: 'data-for',
            breakpoint: 1310
        };

        this.settings = Object.assign({}, defaults, options);

        this.$navigation = element;
        this.$navigationToggle = document.querySelector('[' + this.settings.initAttr + '="' + this.settings.rootToggleAttr + '"]');
        this.$navigationScroller = this.$navigation.querySelector('[' + this.settings.initAttr + '="' + this.settings.scrollerAttr + '"]');
        this.$navigationToggles = this.$navigation.querySelectorAll('[' + this.settings.initAttr + '="' + this.settings.toggleAttr + '"]');
        this.$navigationMainWithChildren = this.$navigation.querySelectorAll('[' + this.settings.initAttr + '="' + this.settings.childrenAttr + '"]');
        this.$$navigationBackToLevel = this.$navigation.querySelectorAll('[' + this.settings.a11yLabelAttr + ']');
        this.$mainNavLevelOne = this.$navigation.querySelector('[data-nav-level-one]');
        this.$$navigationNextLevel = this.$navigation.querySelectorAll('[data-nav="next-level"]');
        this.$body = document.querySelector('body');
        this.closeTimer = null;
        this.focusedRootElement = null;
        this.focusTimer = null;
        this.currentFocused = null;
        this.navActive = false;
        this.lastFocusElement = null;

        this.initialize();
    }

    initialize () {
        this.setEvents();

        if (this.$mainNavLevelOne) {
            this.resizeMainNav();
        }
    }

    setEvents () {
        const breakpoint = window.matchMedia('(min-width: ' + this.settings.breakpoint + 'px)');

        this.$navigation.addEventListener('change', (e) => {
            const $element = e.target;

            if ($element === this.$navigationToggle) {
                if (this.$navigationToggle.checked === true) {
                    this.clearAllSubnavs();
                    disableBodyScroll(this.$navigationScroller, { reserveScrollBarGap: true });
                } else {
                    if (this.$mainNavLevelOne) {
                        this.$mainNavLevelOne.style.height = 'auto';
                    }

                    enableBodyScroll(this.$navigationScroller);
                }

                this.navActive = this.$navigationToggle.checked;
            }

            if ($element.getAttribute('data-nav') === 'toggle') {
                const $parent = $element.closest('[data-nav="category"]');
                if ($parent) {
                    if ($element.checked === true) {
                        $parent.setAttribute('aria-expanded', true);
                    } else {
                        $parent.setAttribute('aria-expanded', false);
                    }
                }
            }

            if ($element.getAttribute(this.settings.initAttr) === this.settings.toggleAttr) {
                this.$navigationScroller.scrollTo(0, 0);
            }
        });

        // enter fires change event
        this.$navigation.addEventListener('keydown', (e) => {
            let isEnter = false;
            if ('key' in e) {
                isEnter = (e.key === 'Enter');
            } else {
                isEnter = (e.keyCode === 13);
            }

            if (isEnter) {
                const $element = e.target;
                if ($element.tagName.toLowerCase() === 'input' && $element.type === 'checkbox' && document.activeElement === $element) {
                    this.toggleInputChecked($element, !$element.checked);
                }
            }
        });

        if (this.$$navigationBackToLevel.length > 0) {
            this.$$navigationBackToLevel.forEach($backToLevelButton => {
                $backToLevelButton.onclick = () => {
                    this.toggleInputChecked(document.getElementById($backToLevelButton.dataset.for), false);
                    this.$navigation.querySelector(`[data-nav-toggle="${$backToLevelButton.dataset.for}"]`).focus();
                };
            });
        }

        if (this.$$navigationNextLevel.length > 0) {
            this.$$navigationNextLevel.forEach($nextLevelButton => {
                const $relatedCheckbox = document.getElementById($nextLevelButton.dataset.navToggle);

                window.addEventListener('resize', debounce(() => {
                    detectMouseEventForA11y();
                }, 100));

                const detectMouseEventForA11y = () => {
                    if (breakpoint.matches) {
                        if ($nextLevelButton.dataset.navHoverActive === 'false') {
                            $nextLevelButton.dataset.navHoverActive = 'true';
                            $nextLevelButton.parentElement.onmouseenter = () => {
                                if (this.currentFocused !== $nextLevelButton) {
                                    $nextLevelButton.click();
                                }
                            };
                        }
                    } else {
                        if ($nextLevelButton.dataset.navHoverActive === 'true') {
                            $nextLevelButton.dataset.navHoverActive = 'false';
                            $nextLevelButton.parentElement.onmouseenter = null;
                        }

                        $nextLevelButton.setAttribute('aria-pressed', 'false');
                        this.currentFocused === null;
                    }
                };

                detectMouseEventForA11y();

                $nextLevelButton.onclick = () => {
                    this.toggleInputChecked($relatedCheckbox, true);
                    const nextLevelBackButton = document.querySelector(`[data-for="${$nextLevelButton.dataset.navToggle}"]`);

                    if (breakpoint.matches) {
                        if (this.currentFocused !== $nextLevelButton) {
                            if (this.currentFocused !== null) {
                                const $currentCheckbox = document.getElementById(this.currentFocused.dataset.navToggle);
                                this.toggleInputChecked($currentCheckbox, false);
                                this.currentFocused.setAttribute('aria-pressed', 'false');
                            }
                        }

                        $nextLevelButton.setAttribute('aria-pressed', $relatedCheckbox.checked);
                        this.currentFocused = $nextLevelButton;
                    }

                    if (nextLevelBackButton) {
                        nextLevelBackButton.focus();
                    }
                };
            });
        }

        for (let i = 0; i < this.$navigationMainWithChildren.length; i++) {
            const $rootItem = this.$navigationMainWithChildren[i];
            const $closeSubnavHandle = $rootItem.querySelector('[' + this.settings.initAttr + '="' + this.settings.closeAttr + '"]');
            const $subScroller = $rootItem.querySelector('[' + this.settings.initAttr + '="' + this.settings.subScrollerAttr + '"]');

            $rootItem.addEventListener('mouseenter', () => {
                if (breakpoint.matches) {
                    this.$body.classList.add('nav--open');
                    $rootItem.setAttribute('aria-expanded', true);
                    $subScroller.scrollTo(0, 0);
                    disableBodyScroll($subScroller, { reserveScrollBarGap: true });
                    this.navActive = true;
                }
            });

            $rootItem.addEventListener('mouseleave', () => {
                if (breakpoint.matches) {
                    $rootItem.classList.remove('focus-within');
                    this.$body.classList.remove('nav--open');
                    $rootItem.setAttribute('aria-expanded', false);
                    const $nextLevel = $rootItem.querySelector('[data-nav="next-level"]');

                    if ($nextLevel) {
                        $nextLevel.setAttribute('aria-pressed', false);
                    }

                    this.clearAllSubnavs();
                    enableBodyScroll($subScroller);
                }
            });

            $rootItem.addEventListener('focusin', () => {
                this.focusedRootElement = $rootItem;

                if (breakpoint.matches) {
                    this.navActive = true;
                    $rootItem.setAttribute('aria-expanded', true);
                    this.closeOtherSubnavs($rootItem);
                    clearTimeout(this.focusTimer);
                }
            });

            $rootItem.addEventListener('focusout', () => {
                if (breakpoint.matches) {
                    $rootItem.setAttribute('aria-expanded', false);
                }

                clearTimeout(this.focusTimer);
                this.lastFocusElement = this.focusedRootElement;

                this.focusTimer = window.setTimeout(() => {
                    this.focusedRootElement = null;
                }, 500);
            });

            $closeSubnavHandle.addEventListener('click', (e) => {
                const targetId = $closeSubnavHandle.getAttribute('for');
                const $target = document.getElementById(targetId);

                if (breakpoint.matches) {
                    this.$body.classList.remove('nav--open');
                    this.closeMainnav($rootItem);

                    if ($target && $target.checked === false) {
                        e.preventDefault();
                    }
                }
            });
        }

        document.addEventListener('click', (e) => {
            if (breakpoint.matches && e.target.classList.contains('main-nav--level-2') && e.target.classList.contains('main-nav--level-3')) {
                const $rootItem = e.target.closest('[' + this.settings.initAttr + '="' + this.settings.childrenAttr + '"]');
                this.closeMainnav($rootItem);
            }
        });

        window.addEventListener('resize', debounce(() => {
            if (!window.isMobileDevice) {
                this.$navigationToggle.checked = false;
                enableBodyScroll(this.$navigationScroller);
            }
        }, 100));

        // close navigation on ESC key
        document.addEventListener('keydown', (e) => {
            let isEscape = false;
            // let isLeft = false;
            // let isRight = false;
            let isUp = false;
            let isDown = false;

            if ('key' in e) {
                isEscape = (e.key === 'Escape' || e.key === 'Esc');
                // isLeft = (e.key === 'ArrowLeft');
                // isRight = (e.key === 'ArrowRight');
                isUp = (e.key === 'ArrowUp');
                isDown = (e.key === 'ArrowDown');
            } else {
                isEscape = (e.keyCode === 27);
                // isLeft = (e.keyCode === 37);
                // isRight = (e.keyCode === 39);
                isUp = (e.keyCode === 38);
                isDown = (e.keyCode === 40);
            }

            if (isEscape) {
                if (breakpoint.matches) {
                    this.closeMainnav(this.focusedRootElement);
                    if (this.focusedRootElement) {
                        this.focusedRootElement.classList.remove('focus-within');
                    }
                } else {
                    this.clearAllSubnavs();
                    this.toggleInputChecked(this.$navigationToggle, false);
                }
            }

            if (breakpoint.matches) {
                if (this.focusedRootElement !== null) {
                    if (isDown) {
                        this.focusedRootElement.classList.add('focus-within');
                    }
                    if (isUp) {
                        this.focusedRootElement.classList.remove('focus-within');
                    }
                }
            }

            if (this.navActive === true) {
                if (event.code === 'Tab' || event.code === 'Tabs') {
                    if (!this.$navigation.contains(event.target)) {
                        if (this.lastFocusElement !== null) {
                            this.lastFocusElement.classList.remove('focus-within');
                        }

                        this.clearAllSubnavs();
                        this.toggleInputChecked(this.$navigationToggle, false);
                    }
                }
            }
        });
    }

    toggleInputChecked ($input, state = true) {
        $input.checked = state;

        const toggleInputCheckedEvent = new CustomEvent('change', {
            view: window,
            bubbles: true,
            cancelable: false
        });

        $input.dispatchEvent(toggleInputCheckedEvent);
    }

    blurElement ($item) {
        const mouseLeaveEvent = new CustomEvent('mouseleave', {
            view: window,
            bubbles: true,
            cancelable: false
        });

        $item.dispatchEvent(mouseLeaveEvent);
    }

    closeMainnav ($item) {
        clearTimeout(this.closeTimer);

        if ($item) {
            $item.classList.add('inactive');

            this.blurElement($item);

            this.closeTimer = window.setTimeout(() => {
                $item.classList.remove('inactive');

                console.log($item);

                const nextTabElement = $item.querySelector('a');

                if (nextTabElement) {
                    nextTabElement.focus();
                }
            }, 500);
        }
    }

    closeOtherSubnavs ($item) {
        this.$navigationMainWithChildren.forEach($parent => {
            if ($parent !== $item) {
                $parent.classList.remove('focus-within');
            }
        });
    }

    clearAllSubnavs () {
        for (let i = 0; i < this.$navigationToggles.length; i++) {
            const $toggle = this.$navigationToggles[i];

            if ($toggle.checked === true) {
                $toggle.checked = false;
            }
        }
    }

    resizeMainNav () {
        const $allSubNavs = this.$navigation.querySelectorAll('[data-nav="toggle"]');

        const calcHeight = (element) => {
            const subNavContent = element.parentElement.querySelector('[data-subnav-content]');
            let calcHeight = subNavContent.offsetHeight;

            // to prevent minus px set to 0
            if (calcHeight <= 0) {
                calcHeight = 0;
            }

            return calcHeight;
        };

        const handleResize = () => {
            if (window.innerWidth < this.settings.breakpoint) {
                let subNavHeight = 'auto';

                $allSubNavs.forEach($otherSubNav => {
                    if ($otherSubNav.checked) {
                        subNavHeight = `${calcHeight($otherSubNav)}px`;
                    }
                });

                this.$mainNavLevelOne.style.height = subNavHeight;
            } else {
                this.$mainNavLevelOne.style.height = 'auto';
            }
        };

        // Initial call to handleResize
        handleResize();

        // Recall handleResize
        window.addEventListener('resize', debounce(() => {
            this.$mainNavLevelOne.style.height = 'auto';
        }, 100));

        $allSubNavs.forEach($subNav => {
            $subNav.addEventListener('change', () => {
                handleResize();
            });
        });
    }
}

export default Navigation;
